import { Button } from 'react-bootstrap'
import React, { useMemo, useCallback } from 'react'
import { useFormikContext } from 'formik'
import { mdiArrowRightBoldCircleOutline, mdiRestore } from '@mdi/js'
import Icon from '@mdi/react'

const capitalizeFirstLetter = str => {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const LanguageAssociation = ({ surveyContext, currentLanguageMap, originalLanguageMap }) => {
  const { setFieldValue, values } = useFormikContext()

  // Validate surveyContext exists
  if (!surveyContext?.context?.id || !surveyContext?.language) {
    return null
  }

  const surveyLanguageCode = surveyContext.language.code
  const contextId = surveyContext.context.id

  // Get arrays of language contexts for this context ID (supports multiple languages per context)
  const currentLanguages = useMemo(() => {
    return currentLanguageMap.get(contextId) || []
  }, [currentLanguageMap, contextId])

  const originalLanguages = useMemo(() => {
    return originalLanguageMap.get(contextId) || []
  }, [originalLanguageMap, contextId])

  // Find the specific language match in current student languages
  const currentMatch = useMemo(() => {
    return currentLanguages.find(lc => lc.language?.code === surveyLanguageCode)
  }, [currentLanguages, surveyLanguageCode])

  // Find the specific language match in original student languages
  const originalMatch = useMemo(() => {
    return originalLanguages.find(lc => lc.language?.code === surveyLanguageCode)
  }, [originalLanguages, surveyLanguageCode])

  // Check if this specific language was added (exists now but not originally)
  const isNewlyAdded = useMemo(() => {
    return currentMatch && !originalMatch
  }, [currentMatch, originalMatch])

  // Check if this specific language was removed (existed originally but not now)
  const wasRemoved = useMemo(() => {
    return !currentMatch && originalMatch
  }, [currentMatch, originalMatch])

  // Survey language already exists in student record for this context
  const surveyMatchesStudent = useMemo(() => {
    return !!currentMatch
  }, [currentMatch])

  const addLanguageToContext = useCallback(() => {
    if (!values?.languageContexts || !Array.isArray(values.languageContexts)) {
      setFieldValue('languageContexts', [surveyContext])
      return
    }

    // Add this language to the student's language contexts (allows duplicates per context)
    const updated = [...values.languageContexts, surveyContext]
    setFieldValue('languageContexts', updated)
  }, [values?.languageContexts, setFieldValue, surveyContext])

  const removeLanguageFromContext = useCallback(() => {
    if (!values?.languageContexts || !Array.isArray(values.languageContexts)) {
      return
    }

    // Remove this specific language from this specific context
    const updated = values.languageContexts.filter(lc => !(lc?.context?.id === contextId && lc?.language?.code === surveyLanguageCode))
    setFieldValue('languageContexts', updated)
  }, [values?.languageContexts, setFieldValue, contextId, surveyLanguageCode])

  const restoreOriginalLanguage = useCallback(() => {
    if (!originalMatch) return

    if (!values?.languageContexts || !Array.isArray(values.languageContexts)) {
      setFieldValue('languageContexts', [originalMatch])
      return
    }

    // If currently exists, do nothing (it's already there)
    if (currentMatch) {
      return
    }

    // Add back the original language
    const updated = [...values.languageContexts, originalMatch]
    setFieldValue('languageContexts', updated)
  }, [values?.languageContexts, setFieldValue, originalMatch, currentMatch])

  return (
    <div className="row">
      <div className="col-2">
        {surveyContext?.language?.language || 'Unknown'} ({surveyContext?.language?.code?.toUpperCase() || 'N/A'})
      </div>
      <div className="col-1">
        <Button
          size="sm"
          className="language-button"
          variant="outline-primary"
          hidden={surveyMatchesStudent}
          onClick={addLanguageToContext}
          aria-label={`Add ${surveyContext?.language?.language || 'language'} to student record for ${capitalizeFirstLetter(
            surveyContext?.context?.context || ''
          )} context`}
          title="Add survey language to student record"
        >
          <Icon path={mdiArrowRightBoldCircleOutline} size={1} aria-hidden="true" />
        </Button>
      </div>
      <div className="col-4">{capitalizeFirstLetter(surveyContext?.context?.context || 'Unknown Context')}</div>
      <div className="col-1">
        <Button
          size="sm"
          className="language-button"
          variant="outline-danger"
          hidden={!currentMatch}
          onClick={removeLanguageFromContext}
          aria-label={`Remove ${surveyContext?.language?.language || 'language'} from student record for ${capitalizeFirstLetter(
            surveyContext?.context?.context || ''
          )} context`}
          title="Remove this language from student record"
        >
          ×
        </Button>
      </div>
      <div className="col-1">
        <Button
          size="sm"
          className="language-button"
          variant="outline-secondary"
          hidden={!wasRemoved}
          onClick={restoreOriginalLanguage}
          aria-label={`Restore ${surveyContext?.language?.language || 'language'} to student record for ${capitalizeFirstLetter(
            surveyContext?.context?.context || ''
          )} context`}
          title="Restore this language (was removed)"
        >
          <Icon path={mdiRestore} size={1} aria-hidden="true" />
        </Button>
      </div>
      <div className="col-1" aria-live="polite" aria-atomic="true">
        {currentMatch && (
          <>
            {isNewlyAdded && <span className="text-success fw-bold">+ </span>}
            <span aria-label={`Current: ${surveyContext?.language?.code?.toUpperCase() || 'N/A'}`}>
              {surveyContext?.language?.code?.toUpperCase() || 'N/A'}
            </span>
          </>
        )}
        {!currentMatch && (
          <span className="text-muted" aria-label="Not in student record">
            —
          </span>
        )}
      </div>
    </div>
  )
}

const UpdateLanguages = ({ surveyResponse }) => {
  const { initialValues, values, setFieldValue } = useFormikContext()

  // Helper to validate and filter language contexts
  const validateLanguageContexts = useCallback(contexts => {
    if (!Array.isArray(contexts)) return []
    return contexts.filter(
      ctx => ctx?.context?.id && ctx?.language?.code && typeof ctx.language.code === 'string' && ctx.language.code.trim() !== ''
    )
  }, [])

  // Pre-compute language maps once for all child components - O(1) context lookup, supports multiple languages per context
  const languageMaps = useMemo(() => {
    const currentMap = new Map() // Map<contextId, LanguageContext[]>
    const originalMap = new Map() // Map<contextId, LanguageContext[]>

    if (values?.languageContexts && Array.isArray(values.languageContexts)) {
      values.languageContexts.forEach(lc => {
        if (lc?.context?.id) {
          if (!currentMap.has(lc.context.id)) {
            currentMap.set(lc.context.id, [])
          }
          currentMap.get(lc.context.id).push(lc)
        }
      })
    }

    if (initialValues?.languageContexts && Array.isArray(initialValues.languageContexts)) {
      initialValues.languageContexts.forEach(lc => {
        if (lc?.context?.id) {
          if (!originalMap.has(lc.context.id)) {
            originalMap.set(lc.context.id, [])
          }
          originalMap.get(lc.context.id).push(lc)
        }
      })
    }

    return { currentMap, originalMap }
  }, [values?.languageContexts, initialValues?.languageContexts])

  // Validate required data exists
  const surveyLanguages = surveyResponse?.languageContexts || []

  // Early return if no survey languages to display
  if (!surveyLanguages || surveyLanguages.length === 0) {
    return (
      <section aria-labelledby="languages-spoken-heading">
        <div className="input-group-text row text-white bg-primary">
          <h5 id="languages-spoken-heading">Languages Spoken</h5>
        </div>
        <div className="input-group-text row alert-warning" role="note">
          No languages found in survey response
        </div>
      </section>
    )
  }

  return (
    <section aria-labelledby="languages-spoken-heading">
      <div className="input-group-text row text-white bg-primary">
        <h5 id="languages-spoken-heading">Languages Spoken</h5>
      </div>
      <div className="input-group-text row alert-info" role="note">
        Add or remove languages from student record
      </div>
      <div className="input-group-text row bg-secondary text-left" role="toolbar" aria-label="Language actions toolbar">
        <div className="col-2 flex-justify-align">
          <Button
            size="sm"
            variant="primary"
            onClick={() => {
              // Add all survey languages that don't already exist in student record
              const currentContexts = values?.languageContexts || []
              // Create a Set of existing "contextId-languageCode" combinations for fast lookup
              const existingCombos = new Set(currentContexts.map(lc => `${lc.context?.id}-${lc.language?.code}`))

              // Add survey languages that aren't already present
              const newLanguages = surveyLanguages.filter(surveyLang => {
                const combo = `${surveyLang.context?.id}-${surveyLang.language?.code}`
                return !existingCombos.has(combo)
              })

              const updated = [...currentContexts, ...newLanguages]
              setFieldValue('languageContexts', validateLanguageContexts(updated))
            }}
            aria-label="Add all survey languages to student record"
            title="Add all languages from survey (won't create duplicates)"
          >
            Use All
          </Button>
        </div>
        <div className="col-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setFieldValue('languageContexts', validateLanguageContexts(initialValues?.languageContexts || []))}
            aria-label="Reset all languages to original student values"
            title="Undo all changes and restore original values"
          >
            Reset
          </Button>
        </div>
        <div className="col-6 text-center" aria-label="Current selection indicator">
          Selected Languages
        </div>
      </div>
      {surveyLanguages
        .filter(context => context?.context?.id && context?.language?.code)
        .map(context => (
          <LanguageAssociation
            key={`ctx-${context.context.id}-lang-${context.language.code}`}
            surveyContext={context}
            currentLanguageMap={languageMaps.currentMap}
            originalLanguageMap={languageMaps.originalMap}
          />
        ))}
    </section>
  )
}
export default UpdateLanguages
